import { supabase } from '@/lib/supabase';
import { FunctionsHttpError } from '@supabase/supabase-js';

/** If true, branch/account assignment uses only RPCs; no direct DML. Set false only for dev if RPCs not deployed. */
const REQUIRE_RPCS_FOR_ACCESS_SETTINGS = true;

const RPC_MIGRATION_HINT =
  'Server is missing required RPCs. Apply migrations: identity_model_auth_user_id.sql then rpc_user_branches_accounts_auth_id_only.sql';

/** Extract error message from Edge Function non-2xx response body */
async function getFunctionErrorMessage(err: unknown): Promise<string | null> {
  if (err instanceof FunctionsHttpError && err.context) {
    try {
      const body = (await err.context.json()) as { error?: string };
      return body?.error ?? null;
    } catch {
      return null;
    }
  }
  return null;
}

export interface User {
  id: string;
  company_id: string;
  email: string;
  full_name: string;
  role: string;
  phone?: string;
  code?: string; // Auto-generated USR-XXX (trigger). Shown in User Management.
  user_code?: string; // Same as code; kept for compatibility.
  is_active: boolean;
  can_be_assigned_as_salesman?: boolean;
  /** Default commission % when this user is assigned as salesman (SaleForm uses for new sales). */
  default_commission_percent?: number | null;
  auth_user_id?: string | null; // Links to auth.users.id. Null = no login yet.
  last_login_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

export const userService = {
  // Get all users for a company
  async getAllUsers(companyId: string, options?: {
    includeInactive?: boolean;
    role?: string;
    canBeSalesman?: boolean;
  }) {
    let query = supabase
      .from('users')
      .select('*')
      .eq('company_id', companyId)
      .order('full_name');

    // Filter by active status
    if (!options?.includeInactive) {
      query = query.eq('is_active', true);
    }

    // Filter by role if specified
    if (options?.role) {
      query = query.eq('role', options.role);
    }

    const { data, error } = await query;
    if (error) throw error;

    // Filter by salesman permission if specified
    if (options?.canBeSalesman) {
      return (data || []).filter(user => {
        const role = String(user.role || '').toLowerCase();
        return (
          user.can_be_assigned_as_salesman === true ||
          role === 'salesman' ||
          role === 'admin' ||
          role === 'owner' ||
          role === 'manager'
        );
      });
    }

    return data || [];
  },

  // Get single user
  async getUser(id: string) {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  },

  /** Load ERP profile for the signed-in auth user (id OR auth_user_id match). */
  async getProfileForAuthUser(authUserId: string, companyId?: string | null): Promise<User | null> {
    if (!authUserId) return null;
    let query = supabase
      .from('users')
      .select('*')
      .or(`id.eq.${authUserId},auth_user_id.eq.${authUserId}`)
      .limit(1);
    if (companyId) {
      query = query.eq('company_id', companyId);
    }
    const { data, error } = await query.maybeSingle();
    if (error) throw error;
    return data as User | null;
  },

  /** Self-service profile update: full_name and phone only. */
  async updateOwnProfile(
    authUserId: string,
    companyId: string | null,
    payload: { full_name: string; phone?: string | null },
  ): Promise<User> {
    const erpUserId = await this.resolvePublicUserId(companyId, authUserId);
    const updatePayload = {
      full_name: payload.full_name.trim(),
      phone: payload.phone?.trim() || null,
      updated_at: new Date().toISOString(),
    };
    const { data, error } = await supabase
      .from('users')
      .update(updatePayload)
      .eq('id', erpUserId)
      .select()
      .maybeSingle();

    if (error) throw error;
    if (!data) {
      throw new Error(
        'Profile update was not applied (no matching row or insufficient permission).',
      );
    }
    return data as User;
  },

  // Generate next user code
  async generateUserCode(companyId: string): Promise<string> {
    try {
      // Get max code and increment
      const { data: users } = await supabase
        .from('users')
        .select('user_code')
        .eq('company_id', companyId)
        .not('user_code', 'is', null)
        .order('user_code', { ascending: false })
        .limit(1);

      if (users && users.length > 0 && users[0].user_code) {
        const lastCode = users[0].user_code;
        const match = lastCode.match(/USR-(\d+)/);
        if (match) {
          const nextNum = parseInt(match[1]) + 1;
          return `USR-${String(nextNum).padStart(3, '0')}`;
        }
      }
      
      // Default: start from USR-001
      return 'USR-001';
    } catch (error) {
      console.error('[USER SERVICE] Error generating user code:', error);
      // Fallback
      return 'USR-001';
    }
  },

  /**
   * Create user with Auth + assign branch/account access in one call (no manual SQL for admin).
   * Edge Function create-erp-user: auth user + profile + user_branches + user_account_access (auth_user_id only).
   */
  async createUserWithAuth(params: {
    email: string;
    full_name: string;
    role: string;
    company_id: string;
    phone?: string;
    is_salesman?: boolean;
    is_active?: boolean;
    temporary_password?: string;
    send_invite_email?: boolean;
    branch_ids?: string[];
    account_ids?: string[];
    default_branch_id?: string;
  }) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error('Not authenticated. Please log in to create users.');
    }
    const { data, error } = await supabase.functions.invoke('create-erp-user', {
      body: params,
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });
    if (error) {
      const msg = await getFunctionErrorMessage(error);
      throw new Error(msg || error.message);
    }
    const result = data as {
      success?: boolean;
      error?: string;
      user_id?: string;
      auth_user_id?: string;
      created?: boolean;
      assignedBranchesCount?: number;
      assignedAccountsCount?: number;
    };
    if (!result?.success) throw new Error(result?.error || 'Failed to create user');
    return result;
  },

  /** Create user (legacy): ERP profile only. Use createUserWithAuth when Edge Function available. */
  async createUser(user: Partial<User>) {
    const insertPayload: any = { ...user };
    const validId = insertPayload.id && typeof insertPayload.id === 'string' && insertPayload.id.length > 0;
    insertPayload.id = validId ? insertPayload.id : crypto.randomUUID();
    if (!insertPayload.user_code && insertPayload.company_id) {
      insertPayload.user_code = await this.generateUserCode(insertPayload.company_id);
    }
    delete insertPayload.permissions;
    const { data, error } = await supabase.from('users').insert(insertPayload).select().single();
    if (error) throw error;
    return data;
  },

  // Update user
  async updateUser(id: string, updates: Partial<User>) {
    console.log('[USER SERVICE] Updating user:', id, 'with payload:', JSON.stringify(updates, null, 2));
    
    try {
      // Prepare update payload
      const updatePayload: any = { ...updates };
      delete updatePayload.permissions;
      
      const { data, error } = await supabase
        .from('users')
        .update(updatePayload)
        .eq('id', id)
        .select()
        .maybeSingle();

      if (error) {
        console.error('[USER SERVICE] Error updating user:', error);
        console.error('[USER SERVICE] Error details:', {
          message: error.message,
          details: error.details,
          hint: error.hint,
          code: error.code
        });
        throw error;
      }

      if (!data) {
        const denied = new Error(
          'User update was not applied (no matching row or insufficient permission). Admin or owner role required.',
        );
        (denied as { code?: string }).code = 'PGRST116';
        throw denied;
      }
      
      console.log('[USER SERVICE] User updated successfully:', data);
      return data;
    } catch (error: any) {
      console.error('[USER SERVICE] Exception updating user:', error);
      throw error;
    }
  },

  // Delete user (soft delete by setting is_active to false)
  async deleteUser(id: string) {
    const { data, error } = await supabase
      .from('users')
      .update({ is_active: false })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // Get users who can be assigned as salesman.
  // Commission: use users.default_commission_percent; if null, fall back to employees.commission_rate so Edit User (employees) and SaleForm stay in sync.
  async getSalesmen(companyId: string) {
    const users = await this.getAllUsers(companyId, {
      includeInactive: false,
      canBeSalesman: true
    });
    if (!users?.length) return users || [];
    const userIds = users.map((u: any) => u.id);
    const { data: employees } = await supabase
      .from('employees')
      .select('user_id, commission_rate')
      .in('user_id', userIds);
    const commissionByUserId = new Map<string, number>();
    (employees || []).forEach((emp: any) => {
      if (emp.user_id != null && emp.commission_rate != null) {
        commissionByUserId.set(emp.user_id, Number(emp.commission_rate));
      }
    });
    return users.map((u: any) => ({
      ...u,
      default_commission_percent:
        u.default_commission_percent != null
          ? Number(u.default_commission_percent)
          : (commissionByUserId.get(u.id) ?? null),
      rental_commission_percent:
        u.rental_commission_percent != null
          ? Number(u.rental_commission_percent)
          : null,
    }));
  },

  /** Admin: Send password reset email to user */
  async sendResetEmail(userId: string) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('Not authenticated');
    const u = await this.getUser(userId);
    const { data, error } = await supabase.functions.invoke('user-admin-actions', {
      body: { action: 'send_reset_email', user_id: userId, email: u?.email },
      headers: { Authorization: `Bearer ${session.access_token}` },
    });
    if (error) {
      const msg = await getFunctionErrorMessage(error);
      throw new Error(msg || error.message);
    }
    const r = data as { success?: boolean; error?: string };
    if (!r?.success) throw new Error(r?.error || 'Failed to send reset email');
  },

  /** Admin: Set new password for user */
  async resetPassword(userId: string, newPassword: string) {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) throw new Error('Not authenticated');
    const { data, error } = await supabase.functions.invoke('user-admin-actions', {
      body: { action: 'reset_password', user_id: userId, new_password: newPassword },
      headers: { Authorization: `Bearer ${session.access_token}` },
    });
    if (error) {
      const msg = await getFunctionErrorMessage(error);
      throw new Error(msg || error.message);
    }
    const r = data as { success?: boolean; error?: string };
    if (!r?.success) throw new Error(r?.error || 'Failed to reset password');
  },

  /**
   * Get active users eligible for salary (Staff, Salesman, Operator, Admin, Manager, Owner).
   * Used in Expenses → Salary: "Pay to (User)" dropdown. Workers (Dyer, Stitcher, etc.) are NOT included.
   */
  async getUsersForSalary(companyId: string): Promise<User[]> {
    const all = await this.getAllUsers(companyId, { includeInactive: false });
    const salaryRoles = ['owner', 'admin', 'manager', 'staff', 'salesman', 'operator', 'cashier', 'inventory'];
    return (all || [])
      .filter((u) => {
        const role = (u.role || '').toLowerCase();
        if (salaryRoles.includes(role)) return true;
        const perms = (u as User & { permissions?: Record<string, unknown> }).permissions ?? {};
        if (perms.canBeAssignedAsSalesman === true) return true;
        if ((perms as { sales?: { canBeAssignedAsSalesman?: boolean } }).sales?.canBeAssignedAsSalesman === true) {
          return true;
        }
        return u.can_be_assigned_as_salesman === true;
      })
      .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '', undefined, { sensitivity: 'base' }));
  },

  /** Get branch IDs assigned to a user (for Edit User → Branch Access). userId = auth_user_id (identity). */
  async getUserBranches(userId: string): Promise<string[]> {
    const { data, error } = await supabase
      .from('user_branches')
      .select('branch_id')
      .eq('user_id', userId);
    if (error) throw error;
    return (data || []).map((r: { branch_id: string }) => r.branch_id);
  },

  /**
   * Resolve to public.users.id via RPC. Not used for branch/account — those use auth_user_id only.
   * Kept for any path that still needs public profile id from auth id.
   */
  async resolvePublicUserId(companyId: string | null, maybeId: string): Promise<string> {
    if (!maybeId) return maybeId;
    if (companyId) {
      const { data, error } = await supabase.rpc('get_public_user_id', {
        p_user_id: maybeId,
        p_company_id: companyId,
      });
      if (!error && data) return data as string;
    }
    const { data, error } = await supabase.from('users').select('id').or(`id.eq.${maybeId},auth_user_id.eq.${maybeId}`).limit(1).maybeSingle();
    if (!error && data?.id) return data.id;
    return maybeId;
  },

  /** Set branch access for a user. Admin only. userId must be auth_user_id (auth.users.id). RPC-only. */
  async setUserBranches(userId: string, branchIds: string[], defaultBranchId?: string, companyId?: string | null): Promise<void> {
    const ids = Array.from(new Set(branchIds)).filter(Boolean);
    const defaultId = defaultBranchId || ids[0] || null;
    const payload = {
      p_user_id: userId,
      p_branch_ids: ids,
      p_default_branch_id: defaultId || null,
      p_company_id: companyId ?? null,
    };
    const { error } = await supabase.rpc('set_user_branches', payload);
    if (!error) return;
    if (REQUIRE_RPCS_FOR_ACCESS_SETTINGS && (error.code === '42883' || error.message?.includes('does not exist'))) {
      throw new Error(RPC_MIGRATION_HINT);
    }
    throw error;
  },

  /** Get account IDs assigned to a user (for Edit User → Account Access). userId = auth_user_id. */
  async getUserAccountAccess(userId: string): Promise<string[]> {
    const { data, error } = await supabase
      .from('user_account_access')
      .select('account_id')
      .eq('user_id', userId);
    if (error) throw error;
    return (data || []).map((r: { account_id: string }) => r.account_id);
  },

  /** Set account access for a user. Admin only. userId must be auth_user_id. RPC-only. */
  async setUserAccountAccess(userId: string, accountIds: string[], companyId?: string | null): Promise<void> {
    const ids = Array.from(new Set(accountIds)).filter(Boolean);
    const payload = {
      p_user_id: userId,
      p_account_ids: ids,
      p_company_id: companyId ?? null,
    };
    const { error } = await supabase.rpc('set_user_account_access', payload);
    if (!error) return;
    if (REQUIRE_RPCS_FOR_ACCESS_SETTINGS && (error.code === '42883' || error.message?.includes('does not exist'))) {
      throw new Error(RPC_MIGRATION_HINT);
    }
    throw error;
  },
};
